/**
 * 主题工具（主题色 + 明暗模式）
 *
 * 由单一主色推导 9 级品牌色阶（命名对齐 50 ~ 800）：
 * - 浅色模式：色阶 = 主色与白色（50~500）/ 黑色（700~800）线性混合
 * - 暗色模式：浅色档改为「主色与暗色表面混合」，深色档保持深色，
 *   并额外提供提亮后的 --brand-text，保证品牌色文字在深色背景上的对比度
 */

/** 明暗模式 */
export type ThemeMode = 'light' | 'dark' | 'auto'

/** 明暗模式选项 */
export const THEME_MODES: { value: ThemeMode; label: string }[] = [
  { value: 'light', label: '亮色' },
  { value: 'dark', label: '暗色' },
  { value: 'auto', label: '跟随系统' }
]

/** 主题预设 */
export interface ThemePreset {
  id: string
  name: string
  color: string
}

/** 预设主题色（首项为深圳大学深大红，即系统默认） */
export const THEME_PRESETS: ThemePreset[] = [
  { id: 'szu', name: '深大红', color: '#950133' },
  { id: 'blue', name: '学术蓝', color: '#1d4ed8' },
  { id: 'teal', name: '松墨绿', color: '#0f766e' },
  { id: 'violet', name: '紫荆紫', color: '#7c3aed' },
  { id: 'orange', name: '荔枝橙', color: '#c2410c' },
  { id: 'graphite', name: '石墨灰', color: '#334155' }
]

/** 默认主题色（深圳大学深大红） */
export const DEFAULT_THEME_COLOR = THEME_PRESETS[0].color

/** 默认明暗模式（保持与改造前一致的亮色界面） */
export const DEFAULT_THEME_MODE: ThemeMode = 'light'

/** 本地存储键名 */
export const THEME_STORAGE_KEY = 'workbench.theme-color'
export const THEME_MODE_STORAGE_KEY = 'workbench.theme-mode'

/** 色阶等级（600 为主色本身） */
const SCALE_STEPS = [50, 100, 200, 300, 400, 500, 600, 700, 800] as const

/** 浅色模式各等级与白色 / 黑色混合的比例；600 为主色，不混合 */
const LIGHT_MIX_RATIO: Record<number, number> = {
  50: 0.94,
  100: 0.85,
  200: 0.68,
  300: 0.46,
  400: 0.22,
  500: 0.1,
  600: 0,
  700: 0.16,
  800: 0.32
}

/** 暗色模式下浅色档与暗色表面的混合比例（值越大越接近暗色表面） */
const DARK_MIX_RATIO: Record<number, number> = {
  50: 0.88,
  100: 0.76,
  200: 0.62,
  300: 0.42,
  400: 0.28,
  500: 0.12,
  600: 0,
  700: 0.16,
  800: 0.32
}

type Rgb = [number, number, number]

const WHITE: Rgb = [255, 255, 255]
const BLACK: Rgb = [0, 0, 0]
/** 与 theme.css 中的 --surface 暗色值保持一致，用于推导暗色品牌色阶 */
const DARK_SURFACE: Rgb = [0x16, 0x1a, 0x20]

/** 规范化颜色：支持 #rgb / #rrggbb（可省略 #），非法值返回 null */
export function normalizeHexColor(input: string): string | null {
  const raw = (input ?? '').trim().replace(/^#/, '').toLowerCase()

  if (/^[0-9a-f]{3}$/.test(raw)) {
    return `#${raw[0]}${raw[0]}${raw[1]}${raw[1]}${raw[2]}${raw[2]}`
  }
  if (/^[0-9a-f]{6}$/.test(raw)) {
    return `#${raw}`
  }
  return null
}

/** hex → RGB 三元组 */
function hexToRgb(hex: string): Rgb {
  const normalized = normalizeHexColor(hex) ?? DEFAULT_THEME_COLOR
  const value = normalized.slice(1)
  return [
    parseInt(value.slice(0, 2), 16),
    parseInt(value.slice(2, 4), 16),
    parseInt(value.slice(4, 6), 16)
  ]
}

/** RGB 三元组 → hex */
function rgbToHex(rgb: Rgb): string {
  return `#${rgb.map((v) => Math.round(v).toString(16).padStart(2, '0')).join('')}`
}

/** 按比例把 a 混向 b（ratio=0 返回 a，ratio=1 返回 b） */
function mix(a: Rgb, b: Rgb, ratio: number): Rgb {
  return [a[0] + (b[0] - a[0]) * ratio, a[1] + (b[1] - a[1]) * ratio, a[2] + (b[2] - a[2]) * ratio]
}

/** 由主色生成品牌色阶（dark=true 时生成暗色模式色阶） */
export function buildBrandScale(color: string, dark = false): Record<number, string> {
  const rgb = hexToRgb(color)
  const ratioTable = dark ? DARK_MIX_RATIO : LIGHT_MIX_RATIO
  const scale: Record<number, string> = {}

  SCALE_STEPS.forEach((step) => {
    const ratio = ratioTable[step] ?? 0
    if (ratio === 0) {
      scale[step] = rgbToHex(rgb)
      return
    }

    if (dark) {
      // 暗色模式：浅色档向暗色表面靠拢，深色档继续加深，其余档向白色提亮
      const target = step <= 300 ? DARK_SURFACE : step <= 500 ? WHITE : BLACK
      scale[step] = rgbToHex(mix(rgb, target, ratio))
      return
    }

    scale[step] = rgbToHex(mix(rgb, step >= 700 ? BLACK : WHITE, ratio))
  })

  return scale
}

/** 主色的 "r, g, b" 字符串（供 rgba(var(--brand-rgb), α) 使用） */
export function hexToRgbTriplet(color: string): string {
  return hexToRgb(color).join(', ')
}

/** 暗色模式下的品牌文字色：向白色提亮，保证在深色表面上的可读性 */
export function buildBrandTextColor(color: string, dark: boolean): string {
  if (!dark) {
    return normalizeHexColor(color) ?? DEFAULT_THEME_COLOR
  }
  return rgbToHex(mix(hexToRgb(color), WHITE, 0.45))
}

/** 解析当前是否应使用暗色（auto = 跟随系统偏好） */
export function resolveIsDark(mode: ThemeMode): boolean {
  if (mode === 'dark') return true
  if (mode === 'light') return false
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false
}

/**
 * 应用主题：写入品牌色阶、品牌文字色，并在暗色模式下给根元素加 data-theme="dark"
 * （一次性同步完成，避免首屏闪色）
 */
export function applyTheme(color: string, mode: ThemeMode = DEFAULT_THEME_MODE): void {
  const normalized = normalizeHexColor(color) ?? DEFAULT_THEME_COLOR
  const dark = resolveIsDark(mode)
  const root = document.documentElement
  const scale = buildBrandScale(normalized, dark)

  Object.entries(scale).forEach(([step, value]) => {
    root.style.setProperty(`--brand-${step}`, value)
  })

  root.style.setProperty('--brand-rgb', hexToRgbTriplet(normalized))
  root.style.setProperty('--brand-ring', `rgba(${hexToRgbTriplet(normalized)}, ${dark ? 0.32 : 0.18})`)
  root.style.setProperty('--brand-text', buildBrandTextColor(normalized, dark))
  // 强调描边：亮色下用主色的深档，暗色下提亮，保证聚焦框在深色背景上可见
  root.style.setProperty('--brand-border', dark ? rgbToHex(mix(hexToRgb(normalized), WHITE, 0.35)) : scale[500])

  if (dark) {
    root.setAttribute('data-theme', 'dark')
  } else {
    root.removeAttribute('data-theme')
  }
}

/** 读取本地保存的主题色（无有效值或读取失败时回退默认色） */
export function readStoredThemeColor(): string {
  try {
    return normalizeHexColor(window.localStorage.getItem(THEME_STORAGE_KEY) ?? '') ?? DEFAULT_THEME_COLOR
  } catch {
    return DEFAULT_THEME_COLOR
  }
}

/** 保存主题色到浏览器本地存储（隐私模式等写入失败时静默忽略） */
export function storeThemeColor(color: string): void {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, color)
  } catch {
    /* 本地存储不可用时仅本次会话生效 */
  }
}

/** 读取本地保存的明暗模式 */
export function readStoredThemeMode(): ThemeMode {
  try {
    const stored = window.localStorage.getItem(THEME_MODE_STORAGE_KEY)
    if (stored === 'light' || stored === 'dark' || stored === 'auto') {
      return stored
    }
    return DEFAULT_THEME_MODE
  } catch {
    return DEFAULT_THEME_MODE
  }
}

/** 保存明暗模式到浏览器本地存储 */
export function storeThemeMode(mode: ThemeMode): void {
  try {
    window.localStorage.setItem(THEME_MODE_STORAGE_KEY, mode)
  } catch {
    /* 本地存储不可用时仅本次会话生效 */
  }
}

/** 判断颜色是否命中预设 */
export function findPresetByColor(color: string): ThemePreset | undefined {
  const normalized = normalizeHexColor(color)
  return THEME_PRESETS.find((preset) => preset.color === normalized)
}
